(() => {
  const homeScrollbarMedia = window.matchMedia('(min-width: 990px)');
  const homeRailSelector = [
    '[data-products-viewport]',
    '[data-category-rail]',
    '[data-designer-rail]',
    '[data-journal-viewport]',
  ].join(',');
  const homeScrollbarStates = new Set();
  const homeScrollbarStateByRail = new WeakMap();
  let homeScrollbarFrame = 0;

  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

  const syncHomeScrollbar = (state) => {
    const { rail, scrollbar, thumb } = state;
    if (!rail.isConnected || !scrollbar.isConnected) return;

    const maximumScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const hasOverflow = maximumScroll > 1;
    scrollbar.hidden = !hasOverflow;
    scrollbar.setAttribute('aria-disabled', String(!hasOverflow));
    scrollbar.setAttribute('aria-valuemax', String(Math.round(maximumScroll)));

    if (!hasOverflow) {
      scrollbar.setAttribute('aria-valuenow', '0');
      scrollbar.setAttribute('aria-valuetext', '0%');
      thumb.style.width = '100%';
      thumb.style.transform = 'translate3d(0, -50%, 0)';
      return;
    }

    const trackWidth = scrollbar.clientWidth;
    if (trackWidth < 1) return;

    const thumbWidth = clamp(trackWidth * (rail.clientWidth / rail.scrollWidth), 32, trackWidth);
    const maximumThumbOffset = Math.max(0, trackWidth - thumbWidth);
    const currentScroll = clamp(rail.scrollLeft, 0, maximumScroll);
    const progress = maximumScroll ? currentScroll / maximumScroll : 0;

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translate3d(${progress * maximumThumbOffset}px, -50%, 0)`;
    scrollbar.setAttribute('aria-valuenow', String(Math.round(currentScroll)));
    scrollbar.setAttribute('aria-valuetext', `${Math.round(progress * 100)}%`);
  };

  const queueHomeScrollbarSync = (state) => {
    if (state.syncFrame) return;
    state.syncFrame = window.requestAnimationFrame(() => {
      state.syncFrame = 0;
      syncHomeScrollbar(state);
    });
  };

  const destroyHomeScrollbar = (state) => {
    state.rail.removeEventListener('scroll', state.onScroll);
    state.rail.removeEventListener('load', state.onContentLoad, true);
    state.scrollbar.removeEventListener('pointerdown', state.onPointerDown);
    state.scrollbar.removeEventListener('keydown', state.onKeyDown);
    state.resizeObserver?.disconnect();
    state.mutationObserver?.disconnect();
    if (state.syncFrame) window.cancelAnimationFrame(state.syncFrame);
    state.pointerCleanup?.();
    state.scrollbar.remove();
    homeScrollbarStateByRail.delete(state.rail);
    homeScrollbarStates.delete(state);
  };

  const createHomeScrollbar = (section, rail) => {
    const existingState = homeScrollbarStateByRail.get(rail);
    if (existingState) {
      if (existingState.scrollbar.isConnected) {
        queueHomeScrollbarSync(existingState);
        return existingState;
      }
      destroyHomeScrollbar(existingState);
    }

    if (!rail.id) {
      rail.id = `PinoHomeRail-${section.id.replace(/[^a-z0-9_-]/gi, '-')}`;
    }

    const scrollbar = document.createElement('div');
    const thumb = document.createElement('span');
    const heading = section.querySelector('h2');
    const isFinnish = document.documentElement.lang.toLowerCase().startsWith('fi');
    const headingText = heading?.textContent.trim().replace(/\s+/g, ' ');
    const fallbackLabel = isFinnish ? 'Vaakasuuntainen vieritys' : 'Horizontal scroll';

    scrollbar.className = 'pino-home-scrollbar';
    scrollbar.tabIndex = 0;
    scrollbar.setAttribute('role', 'scrollbar');
    scrollbar.setAttribute('aria-controls', rail.id);
    scrollbar.setAttribute('aria-label', headingText ? `${headingText} – ${fallbackLabel.toLowerCase()}` : fallbackLabel);
    scrollbar.setAttribute('aria-orientation', 'horizontal');
    scrollbar.setAttribute('aria-valuemin', '0');
    scrollbar.setAttribute('aria-valuemax', '0');
    scrollbar.setAttribute('aria-valuenow', '0');
    scrollbar.setAttribute('aria-valuetext', '0%');
    scrollbar.setAttribute('data-pino-home-scrollbar', '');

    thumb.className = 'pino-home-scrollbar__thumb';
    thumb.setAttribute('aria-hidden', 'true');
    scrollbar.appendChild(thumb);
    rail.insertAdjacentElement('afterend', scrollbar);

    const state = {
      rail,
      scrollbar,
      thumb,
      resizeObserver: null,
      mutationObserver: null,
      pointerCleanup: null,
      syncFrame: 0,
      onScroll: null,
      onContentLoad: null,
      onPointerDown: null,
      onKeyDown: null,
    };

    state.onScroll = () => queueHomeScrollbarSync(state);
    state.onContentLoad = () => queueHomeScrollbarSync(state);
    state.onKeyDown = (event) => {
      if (scrollbar.getAttribute('aria-disabled') === 'true') return;
      const step = Math.max(80, rail.clientWidth * .75);
      let nextScroll = rail.scrollLeft;

      if (event.key === 'ArrowLeft') nextScroll -= step;
      else if (event.key === 'ArrowRight') nextScroll += step;
      else if (event.key === 'Home') nextScroll = 0;
      else if (event.key === 'End') nextScroll = rail.scrollWidth - rail.clientWidth;
      else return;

      event.preventDefault();
      rail.scrollLeft = nextScroll;
    };
    state.onPointerDown = (event) => {
      if (event.button !== 0 || scrollbar.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      scrollbar.focus({ preventScroll: true });
      syncHomeScrollbar(state);

      const maximumScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const trackWidth = scrollbar.clientWidth;
      const thumbWidth = thumb.getBoundingClientRect().width;
      const maximumThumbOffset = Math.max(0, trackWidth - thumbWidth);
      if (!maximumScroll || !maximumThumbOffset) return;

      if (event.target !== thumb) {
        const trackRect = scrollbar.getBoundingClientRect();
        const targetOffset = clamp(event.clientX - trackRect.left - (thumbWidth / 2), 0, maximumThumbOffset);
        rail.scrollLeft = (targetOffset / maximumThumbOffset) * maximumScroll;
        return;
      }

      const pointerId = event.pointerId;
      const startX = event.clientX;
      const startScroll = rail.scrollLeft;
      scrollbar.classList.add('is-dragging');
      const onPointerMove = (moveEvent) => {
        if (moveEvent.pointerId !== pointerId) return;
        moveEvent.preventDefault();
        const scrollDelta = ((moveEvent.clientX - startX) / maximumThumbOffset) * maximumScroll;
        rail.scrollLeft = startScroll + scrollDelta;
      };
      const onPointerEnd = (endEvent) => {
        if (endEvent.pointerId !== pointerId) return;
        state.pointerCleanup?.();
      };
      state.pointerCleanup = () => {
        scrollbar.classList.remove('is-dragging');
        document.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('pointerup', onPointerEnd);
        document.removeEventListener('pointercancel', onPointerEnd);
        state.pointerCleanup = null;
      };

      document.addEventListener('pointermove', onPointerMove, { passive: false });
      document.addEventListener('pointerup', onPointerEnd);
      document.addEventListener('pointercancel', onPointerEnd);
    };

    rail.addEventListener('scroll', state.onScroll, { passive: true });
    rail.addEventListener('load', state.onContentLoad, true);
    scrollbar.addEventListener('pointerdown', state.onPointerDown);
    scrollbar.addEventListener('keydown', state.onKeyDown);

    if ('ResizeObserver' in window) {
      state.resizeObserver = new ResizeObserver(() => queueHomeScrollbarSync(state));
      state.resizeObserver.observe(rail);
      if (rail.firstElementChild) state.resizeObserver.observe(rail.firstElementChild);
    }
    if ('MutationObserver' in window) {
      state.mutationObserver = new MutationObserver(() => queueHomeScrollbarSync(state));
      state.mutationObserver.observe(rail, { childList: true, subtree: true });
    }

    homeScrollbarStateByRail.set(rail, state);
    homeScrollbarStates.add(state);
    queueHomeScrollbarSync(state);
    return state;
  };

  const initHomeScrollbars = (root = document) => {
    const rails = [];
    if (root instanceof Element && root.matches(homeRailSelector)) rails.push(root);
    root.querySelectorAll?.(homeRailSelector).forEach((rail) => rails.push(rail));

    rails.forEach((rail) => {
      const section = rail.closest('.shopify-section') || rail.closest('section');
      if (section) createHomeScrollbar(section, rail);
    });
  };

  initHomeScrollbars();
  document.fonts?.ready.then(() => homeScrollbarStates.forEach(queueHomeScrollbarSync));
  window.addEventListener('resize', () => {
    if (homeScrollbarFrame) return;
    homeScrollbarFrame = window.requestAnimationFrame(() => {
      homeScrollbarFrame = 0;
      homeScrollbarStates.forEach(queueHomeScrollbarSync);
    });
  }, { passive: true });
  const onHomeScrollbarMediaChange = () => {
    if (homeScrollbarMedia.matches) initHomeScrollbars();
    homeScrollbarStates.forEach(queueHomeScrollbarSync);
  };
  if (homeScrollbarMedia.addEventListener) homeScrollbarMedia.addEventListener('change', onHomeScrollbarMediaChange);
  else homeScrollbarMedia.addListener(onHomeScrollbarMediaChange);
  document.addEventListener('shopify:section:load', (event) => {
    window.requestAnimationFrame(() => initHomeScrollbars(event.target));
  });
  document.addEventListener('shopify:section:unload', (event) => {
    homeScrollbarStates.forEach((state) => {
      if (event.target.contains(state.rail) || event.target.contains(state.scrollbar)) destroyHomeScrollbar(state);
    });
  });
})();
